import { useEffect, useState } from "react";
import { API, useAuth } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Leaf, Plus, Pencil, Trash2, Image, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminSuperfoodBlog() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [form, setForm] = useState({ title: "", caption: "", content: "", image_url: "" });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const res = await fetch(`${API}/admin/superfood-blog`, { credentials: "include" });
      if (res.ok) setPosts(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingPost(null);
    setForm({ title: "", caption: "", content: "", image_url: "" });
    setImageFile(null);
    setImagePreview(null);
    setDialogOpen(true);
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setForm({ title: post.title, caption: post.caption, content: post.content, image_url: post.image_url || "" });
    setImageFile(null);
    setImagePreview(post.image_url || null);
    setDialogOpen(true);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.caption.trim()) {
      toast.error("Title and caption are required");
      return;
    }
    setSubmitting(true);
    try {
      let savedPost;
      if (editingPost) {
        const res = await fetch(`${API}/admin/superfood-blog/${editingPost.post_id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(form),
        });
        if (!res.ok) throw new Error("Failed to update");
        savedPost = await res.json();
      } else {
        const res = await fetch(`${API}/admin/superfood-blog`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(form),
        });
        if (!res.ok) throw new Error("Failed to create");
        savedPost = await res.json();
      }

      if (imageFile && savedPost?.post_id) {
        const formData = new FormData();
        formData.append("file", imageFile);
        await fetch(`${API}/admin/superfood-blog/${savedPost.post_id}/image`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });
      }

      toast.success(editingPost ? "Post updated" : "Post created");
      setDialogOpen(false);
      fetchPosts();
    } catch (err) {
      toast.error(err.message);
    }
    setSubmitting(false);
  };

  const toggleActive = async (post) => {
    try {
      await fetch(`${API}/admin/superfood-blog/${post.post_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_active: !post.is_active }),
      });
      fetchPosts();
      toast.success(post.is_active ? "Post hidden" : "Post published");
    } catch { toast.error("Failed to update"); }
  };

  const deletePost = async (postId) => {
    if (!window.confirm("Delete this blog post?")) return;
    try {
      await fetch(`${API}/admin/superfood-blog/${postId}`, {
        method: "DELETE",
        credentials: "include",
      });
      fetchPosts();
      toast.success("Post deleted");
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-superfood-blog">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Leaf size={22} className="text-green-600" /> Superfood Blog
            </h1>
            <p className="text-sm text-gray-500 mt-1">Manage superfood lifestyle content shown on the home page</p>
          </div>
          <Button onClick={openCreate} className="bg-green-700 hover:bg-green-800 text-white" data-testid="create-blog-post-btn">
            <Plus size={16} className="mr-1" /> New Post
          </Button>
        </div>

        {loading ? (
          <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)}</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <Leaf size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No blog posts yet</p>
            <Button onClick={openCreate} variant="outline" className="mt-3 text-green-600 border-green-200" data-testid="create-first-post-btn">
              Create your first post
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {posts.map((post) => (
              <div key={post.post_id} className="bg-white border border-gray-100 rounded-xl p-4 flex gap-4" data-testid={`blog-post-${post.post_id}`}>
                {post.image_url ? (
                  <img src={post.image_url} alt={post.title} className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                    <Image size={20} className="text-green-300" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm">{post.title}</h3>
                      <p className="text-[10px] text-gray-400 mt-0.5 line-clamp-1">{post.caption}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${post.is_active ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400"}`}>
                      {post.is_active ? "Active" : "Hidden"}
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1 line-clamp-2">{post.content}</p>
                  <div className="flex items-center gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => openEdit(post)} className="text-xs h-7" data-testid={`edit-post-${post.post_id}`}>
                      <Pencil size={12} className="mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toggleActive(post)} className={`text-xs h-7 ${post.is_active ? "text-amber-600 border-amber-200" : "text-green-600 border-green-200"}`} data-testid={`toggle-post-${post.post_id}`}>
                      {post.is_active ? <><EyeOff size={12} className="mr-1" /> Hide</> : <><Eye size={12} className="mr-1" /> Publish</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => deletePost(post.post_id)} className="text-xs h-7 text-red-500 border-red-200 hover:bg-red-50" data-testid={`delete-post-${post.post_id}`}>
                      <Trash2 size={12} className="mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Leaf size={18} className="text-green-600" />
                {editingPost ? "Edit Blog Post" : "New Blog Post"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-[10px] text-gray-400">Title *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. The Power of Quinoa"
                  className="mt-1"
                  data-testid="blog-form-title"
                />
              </div>
              <div>
                <Label className="text-[10px] text-gray-400">Caption / Summary *</Label>
                <Input
                  value={form.caption}
                  onChange={(e) => setForm({ ...form, caption: e.target.value })}
                  placeholder="A short catchy line..."
                  className="mt-1"
                  data-testid="blog-form-caption"
                />
              </div>
              <div>
                <Label className="text-[10px] text-gray-400">Content</Label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  placeholder="Full blog text..."
                  rows={4}
                  className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  data-testid="blog-form-content"
                />
              </div>
              <div>
                <Label className="text-[10px] text-gray-400">Image</Label>
                {imagePreview ? (
                  <div className="relative mt-1">
                    <img src={imagePreview} alt="Preview" className="w-full h-40 object-cover rounded-lg" />
                    <button onClick={() => { setImageFile(null); setImagePreview(null); }} className="absolute top-2 right-2 bg-white/80 rounded-full p-1 shadow text-gray-600 hover:text-red-500">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ) : (
                  <label className="mt-1 flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <Image size={24} className="text-gray-400 mb-1" />
                    <span className="text-[10px] text-gray-400">Click to upload image</span>
                    <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" data-testid="blog-form-image-input" />
                  </label>
                )}
              </div>
              <Button onClick={handleSave} disabled={submitting} className="w-full bg-green-700 hover:bg-green-800 text-white" data-testid="blog-form-save-btn">
                {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                {editingPost ? "Update Post" : "Create Post"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
